"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractVarNamesFromVarSegment = exports.compiledNamespace = exports.parsePopulatedWsTopic = exports.wsTopicFromRedisKey = exports.resolvedWsTopicForNamespacePath = exports.asNamespacePath = exports.resolveRedisKeyForNamespacePath = exports.TimeRotatingListPeriodEmbeddedVarSuffix = void 0;
const path_to_regexp_1 = require("path-to-regexp"); // match as matchPath, parse as parsePath
const exceptions_1 = require("../exceptions");
exports.TimeRotatingListPeriodEmbeddedVarSuffix = '/period/:period';
const ScopedDefSegment = 'scopedDef';
const EmbeddedVarMaxChars = 256;
const regexNamespacePathToReplaceSlash = /[\/]/g;
const regexForRemovingShardBraceChars = /[{}]/g;
const regexForConvertingToWsTopic = /[:]/g;
const regexEmbeddedVarPermittedChars = /^[a-z0-9_\-+=@#$%]{1,256}$/i; // hmm, not sure. But NOT colon or slash
/**
 * Builds a redis key that embeds both author-defined variables and info required by platform/runtime (authorEnv and scopedDataDefinitionUid)
 * Optionally wraps one of the author values in a curly-braces for redis sharding. (note: it is 1-indexed)
 * @param args
 */
function resolveRedisKeyForNamespacePath(args) {
    const { primarySegment, varSegment, embeddedDataByVarName, redisShardNdx, scopedDataDefinitionUid, authorEnv } = args;
    let shardedVarName;
    if (typeof redisShardNdx === 'number' && redisShardNdx > 0) {
        const varNames = extractVarNamesFromVarSegment(varSegment);
        shardedVarName = varNames && varNames[redisShardNdx - 1];
    }
    let resolvedPath = _resolveNamespaceSectionToString(asNamespacePath(primarySegment, varSegment), embeddedDataByVarName, ResolveNamespaceTarget.Redis, shardedVarName);
    return `${authorEnv}:${resolvedPath}:scopedDef:${scopedDataDefinitionUid}`;
}
exports.resolveRedisKeyForNamespacePath = resolveRedisKeyForNamespacePath;
function asNamespacePath(primarySegment, varSegment = '') {
    return [
        '/',
        primarySegment,
        varSegment,
    ].join('');
}
exports.asNamespacePath = asNamespacePath;
/**
 * Builds a WsTopic string, embedding both author-defined variables and info required by platform/runtime (authorEnv and scopedDataDefinitionUid)
 * @param args
 */
function resolvedWsTopicForNamespacePath(args) {
    const { authorEnv, scopedDataDefinitionUid, primarySegment, varSegment, embeddedDataByVarName } = args;
    let resolvedPath = _resolveNamespaceSectionToString(asNamespacePath(primarySegment, varSegment), embeddedDataByVarName, ResolveNamespaceTarget.WsTopic);
    return `${authorEnv}/${resolvedPath}/${ScopedDefSegment}/${scopedDataDefinitionUid}`;
}
exports.resolvedWsTopicForNamespacePath = resolvedWsTopicForNamespacePath;
function wsTopicFromRedisKey(redisKey) {
    return redisKey
        .replace(regexForConvertingToWsTopic, '/')
        .replace(regexForRemovingShardBraceChars, '');
}
exports.wsTopicFromRedisKey = wsTopicFromRedisKey;
/**
 * Returns values for embedded vars in a wsTopic.
 * note: removes leading uid
 *   (later: perhaps remove only if present, and return it along with the embedded vars)
 * @param wsTopic
 */
function parsePopulatedWsTopic(wsTopic) {
    const parts = wsTopic.split('/');
    const authorEnv = parts.shift();
    const scopedDataDefinitionUid = parts.pop();
    const scopedDefSegment = parts.pop(); // the "scopedDef" segment
    if (scopedDefSegment !== ScopedDefSegment) {
        throw new exceptions_1.PlusPlusPlus('UnexpectedFormat', {
            reason: 'wsTopic provided does not follow path convention for a data bucket',
            wsTopic,
        });
    }
    const primarySegment = parts.shift();
    const varSegmentParts = [];
    const embeddedDataByVarName = {};
    while (parts.length > 0) {
        const varName = parts.shift();
        const val = parts.shift();
        varSegmentParts.push(`/${varName}/:${varName}`);
        embeddedDataByVarName[varName] = val;
    }
    return {
        authorEnv,
        scopedDataDefinitionUid,
        primarySegment,
        varSegment: varSegmentParts.join(''),
        embeddedDataByVarName,
    };
}
exports.parsePopulatedWsTopic = parsePopulatedWsTopic;
function compiledNamespace(namespacePath, embeddedDataByVarName) {
    Object.keys(embeddedDataByVarName).forEach(varName => {
        const val = embeddedDataByVarName[varName];
        if ((!val || !val.length || val.length > EmbeddedVarMaxChars) && namespacePath.indexOf(`:${varName}`) >= 0) {
            throw new exceptions_1.PlusPlusPlus().appendDetails({
                userMessage: `value length must be 1 to ${EmbeddedVarMaxChars} characters`,
                problemsByProperty: { [varName]: val && val.length ? 'too long' : 'missing' }
            });
        }
    });
    const toPathRaw = path_to_regexp_1.compile(namespacePath);
    let res;
    try {
        res = toPathRaw(embeddedDataByVarName || {});
    }
    catch (err) {
        throw (new exceptions_1.PlusPlusPlus(err, 'FailedToBuildPathWithEmbeddedVars', {
            namespacePath, embeddedDataByVarName
        }));
    }
    return res;
}
exports.compiledNamespace = compiledNamespace;
function extractVarNamesFromVarSegment(varSegment = '') {
    const varNames = [];
    const tokens = varSegment ? path_to_regexp_1.parse(varSegment) : [];
    tokens.forEach((t) => {
        if (t && t.name) {
            varNames.push(t.name);
        }
    });
    return varNames;
}
exports.extractVarNamesFromVarSegment = extractVarNamesFromVarSegment;
var ResolveNamespaceTarget;
(function (ResolveNamespaceTarget) {
    ResolveNamespaceTarget["Redis"] = "Redis";
    ResolveNamespaceTarget["WsTopic"] = "WsTopic";
})(ResolveNamespaceTarget || (ResolveNamespaceTarget = {}));
function _resolveNamespaceSectionToString(namespacePath, embeddedDataByVarName, target, shardedVarName) {
    let res;
    if (!namespacePath) {
        throw new exceptions_1.PlusPlusPlus('NamespacePathRequired');
    }
    Object.values(embeddedDataByVarName).forEach((value) => {
        if (!regexEmbeddedVarPermittedChars.test(value)) {
            throw new exceptions_1.PlusPlusPlus('InvalidEmbeddedValue', {
                value,
                namespacePath,
                embeddedDataByVarName,
                regex: regexEmbeddedVarPermittedChars.toString(),
            });
        }
    });
    if (shardedVarName && target === ResolveNamespaceTarget.Redis) {
        const val = embeddedDataByVarName[shardedVarName];
        if (val) {
            embeddedDataByVarName[shardedVarName] = `{${val}}`;
        }
    }
    try {
        namespacePath = namespacePath.charAt(0) === '/' ? namespacePath : `/${namespacePath}`;
        res = compiledNamespace(namespacePath, embeddedDataByVarName || {});
    }
    catch (err) {
        throw new exceptions_1.PlusPlusPlus('FailedToResolveNamespace', {
            reason: 'Failed to resolve namespace with the provided data',
            namespacePath,
            embeddedDataByVarName,
        });
    }
    res = res.charAt(0) === '/' ? res.substring(1) : res;
    if (target === ResolveNamespaceTarget.Redis) {
        res = res.replace(regexNamespacePathToReplaceSlash, ':');
    }
    else if (target === ResolveNamespaceTarget.WsTopic) {
        res = res.replace(regexForRemovingShardBraceChars, '');
    }
    else {
        throw new exceptions_1.PlusPlusPlus('InvalidNamespaceTarget', {
            target,
            namespacePath
        });
    }
    return res;
}
//# sourceMappingURL=namespace-path-util.js.map